import { useRef, useState, useEffect } from 'react';
import { Mouse, Volume2, VolumeX } from 'lucide-react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useTranslation } from 'react-i18next';
const heroVideo = 'https://ifocus.sa/wp-content/uploads/2026/05/WebsitePromo.mp4';

export const Hero = () => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isMuted, setIsMuted] = useState(true);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start'],
  });

  const y = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const tryPlay = () => {
      video.play().catch(() => {});
    };

    video.addEventListener('loadedmetadata', tryPlay);
    tryPlay();

    return () => {
      video.removeEventListener('loadedmetadata', tryPlay);
    };
  }, []);

  const handleMuteToggle = () => {
    const video = videoRef.current;
    if (!video) return;
    const nowMuted = !isMuted;
    video.muted = nowMuted;
    setIsMuted(nowMuted);
    if (!nowMuted) {
      video.play().catch(() => {
        video.muted = true;
        setIsMuted(true);
      });
    }
  };

  return (
    <section id="hero" ref={containerRef} className="relative min-h-[calc(100vh-var(--topbar-h))] w-full overflow-hidden">
      <div className="absolute inset-0">
        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="w-full h-full object-cover scale-105"
        >
          <source src={heroVideo} type="video/mp4" />
        </video>
        <div className="hero-overlay" />
      </div>

      <motion.div 
        style={{ y, opacity }}
        className="absolute bottom-60 left-0 right-0 z-10"
      >
        <div className="container mx-auto px-6 lg:px-16 text-center">

        </div>
      </motion.div>

      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        onClick={handleMuteToggle}
        className="absolute bottom-10 left-6 lg:left-16 z-10 flex items-center justify-center w-10 h-10 rounded-full bg-background/40 backdrop-blur-md border border-foreground/20 text-foreground/70 hover:text-primary hover:border-primary/50 transition-all duration-300"
        data-testid="button-mute-toggle"
        aria-label={isMuted ? 'Unmute video' : 'Mute video'}
      >
        {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
      </motion.button>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10"
      >
        <motion.a
          href="#showreel"
          className="flex flex-col items-center gap-2 text-foreground/60 hover:text-primary transition-colors group"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        >
          <Mouse className="w-7 h-7 group-hover:text-primary transition-colors" strokeWidth={1.2} />
          <span className="text-xs tracking-widest uppercase font-light">{t('hero.scroll')}</span>
        </motion.a>
      </motion.div>
    </section>
  );
};
